import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useGlobal } from 'reactn';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import { USER_CONTEXT } from '../../constants';
import store, { showToast } from '../core/store';
import { listFormDefinitions, updateFormDefinition } from '../../util/APIUtils';
import { isPermitted } from '../../components/permission-control/permission-control';
import { FORM_CREATE } from './FormPermission';
import { FORMS_COLUMNS, FORMS_PAGE_SIZE, mapFormsToGridData } from './utils/form.util';
import { downloadCsv } from './utils/csvExport.util';
import {
  buildFormsCsvExportPayload,
  mapApiRowsToForms,
} from './utils/forms.util';
import {
  FORMS_LOAD_ERROR,
  FORMS_ROUTE,
  FORMS_UPDATE_ERROR,
  formEditRoute,
  formSubmissionsRoute,
} from './constants/forms.constants';

export const useFormsHook = () => {
  const navigate = useNavigate();
  const [global] = useGlobal();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewForm, setPreviewForm] = useState(null);

  const canCreateForm = useMemo(() => {
    const { cbacConfigFlag, permissions, permissionIds } = global;
    return isPermitted(cbacConfigFlag, permissions, FORM_CREATE, permissionIds);
  }, [global]);

  const fetchFormsSilently = useCallback(async () => {
    const rows = await listFormDefinitions();
    setForms(mapApiRowsToForms(rows));
  }, []);

  const loadForms = useCallback(async () => {
    setLoading(true);
    try {
      await fetchFormsSilently();
    } catch {
      setForms([]);
      store.dispatch(showToast(FORMS_LOAD_ERROR));
    } finally {
      setLoading(false);
    }
  }, [fetchFormsSilently]);

  const handleToggleActive = useCallback(
    async (form) => {
      setLoading(true);
      try {
        await updateFormDefinition(form.id, { is_active: !form.active });
        await fetchFormsSilently();
      } catch {
        store.dispatch(showToast(FORMS_UPDATE_ERROR));
      } finally {
        setLoading(false);
      }
    },
    [fetchFormsSilently],
  );

  const handleViewSubmissions = useCallback(
    (form) => {
      if (form?.id == null || form.id === '') {
        return;
      }
      navigate(formSubmissionsRoute(form.id));
    },
    [navigate],
  );

  const handleEditForm = useCallback(
    (form) => {
      navigate(formEditRoute(form.id));
    },
    [navigate],
  );

  const onPreviewForm = useCallback((form) => {
    setPreviewForm(form);
  }, []);

  const clearPreviewForm = useCallback(() => {
    setPreviewForm(null);
  }, []);

  const handleCreateNewForm = useCallback(() => {
    navigate(FORMS_ROUTE);
  }, [navigate]);

  const actionHandlers = useMemo(
    () => ({
      onToggleActive: handleToggleActive,
      onPreviewForm,
      onViewSubmissions: handleViewSubmissions,
      onEditForm: handleEditForm,
    }),
    [handleToggleActive, onPreviewForm, handleViewSubmissions, handleEditForm],
  );

  const data = useMemo(
    () => mapFormsToGridData(forms, actionHandlers, USER_CONTEXT?.personId),
    [forms, actionHandlers],
  );

  const primaryButtonProps = useMemo(
    () => {
      if (!canCreateForm) {
        return undefined;
      }
      return {
        text: 'Create New Form',
        variant: 'contained',
        startIcon: <AddIcon />,
        onClick: handleCreateNewForm,
      };
    },
    [handleCreateNewForm, canCreateForm],
  );

  const handleExportForms = useCallback(() => {
    downloadCsv(buildFormsCsvExportPayload(forms));
  }, [forms]);

  const secondaryButtonProps = useMemo(
    () => ({
      text: 'Export CSV',
      variant: 'outlined',
      startIcon: <SaveAltIcon />,
      onClick: handleExportForms,
      disabled: loading || forms.length === 0,
    }),
    [handleExportForms, loading, forms.length],
  );

  useEffect(() => {
    loadForms();
  }, [loadForms]);

  return {
    columns: FORMS_COLUMNS,
    data,
    pageSize: FORMS_PAGE_SIZE,
    loading,
    refreshForms: loadForms,
    primaryButtonProps,
    secondaryButtonProps,
    previewForm,
    clearPreviewForm,
  };
};
